/**
 * DAAily Operations request schemas — Oliver's self-merge zone (like
 * analyticsSchemas.ts is Frank's). Operations-scoped zod schemas live HERE,
 * never in the app-wide schemas.ts, so operations changes don't need hub review.
 *
 * This file is the operations barrel: it re-exports the other `operations*.ts`
 * modules so a NEW operations file needs no edit to `shared/src/index.ts`
 * (which is outside the self-merge zone). `index.ts` already does
 * `export * from './operationsSchemas.js'`.
 */
import { z } from 'zod';
export * from './operationsMargin.js';
export * from './operationsMarginSchemas.js';
export * from './operationsNewsFeed.js';
/** Operations admin: grant/revoke a viewer's page access from the module. */
export const OperationsSetUserPagesSchema = z.object({
    targetEmail: z.string().trim().toLowerCase().email(),
    pages: z.array(z.string().trim().min(1).max(40)).max(40),
});
/* ─────────────────────────────────────────────────────────────────────────────
 * Manufacturer audit (operations-service `operationsApi` action payloads).
 * The service validates independently (it's a standalone package, no
 * @daaily/shared import) — keep the two in sync by hand, as analytics-service
 * does with its enums.
 * ──────────────────────────────────────────────────────────────────────────── */
/** Max rows accepted in one segmentCreate call — mirrors MAX_SEGMENT_ROWS. */
export const OPERATIONS_MAX_SEGMENT_ROWS = 12000;
/**
 * Create one audit segment from an uploaded URL list. The hub page parses the
 * CSV/XLSX client-side and posts the rows (original order preserved); `raw`
 * carries the row's other columns so nothing from the source file is lost.
 */
export const OperationsSegmentCreateSchema = z.object({
    action: z.literal('segmentCreate'),
    label: z.string().trim().min(1).max(80),
    rows: z
        .array(z.object({
        url: z.string().trim().max(2048),
        raw: z.record(z.unknown()).optional(),
    }))
        .min(1)
        .max(OPERATIONS_MAX_SEGMENT_ROWS),
    // Cost-approval gate (Oliver, 29 Sep, "via Hub"): opt-in per upload. When
    // true the segment is created PAUSED (`status: 'pending_approval'`) until an
    // admin approves it — see OperationsCostApprovalDecideSchema below.
    requireApproval: z.boolean().optional(),
    estimatedUsd: z.number().min(0).optional(),
    note: z.string().trim().max(500).optional(),
});
/** Approve or reject a segment's cost-approval gate. Admin-only, enforced server-side too. */
export const OperationsCostApprovalDecideSchema = z.object({
    action: z.literal('costApprovalDecide'),
    segmentId: z.string().trim().min(1).max(64),
    approve: z.boolean(),
});
/** List all segments (dashboard). */
export const OperationsSegmentListSchema = z.object({ action: z.literal('segmentList') });
/** One segment + its computed gate + counters. */
export const OperationsSegmentGetSchema = z.object({
    action: z.literal('segmentGet'),
    segmentId: z.string().trim().min(1).max(64),
});
/**
 * A page of a segment's projects, ordered by `seq`. `cursor` is the last `seq`
 * seen (numeric, as a string on the wire). Status / text filtering is done
 * client-side over the loaded page — a segment is bounded at
 * OPERATIONS_MAX_SEGMENT_ROWS, so the whole thing loads in a few pages.
 */
export const OperationsProjectListSchema = z.object({
    action: z.literal('projectList'),
    segmentId: z.string().trim().min(1).max(64),
    cursor: z.union([z.number(), z.string().trim().max(32)]).optional(),
});
//# sourceMappingURL=operationsSchemas.js.map